import React, { useState } from 'react';
import { ThemeContext } from '../../../module/ThemeManager';
import { Container } from '@mui/material';
import { EsNoSubmissions } from '../../EsCards/EsNoSubmissions';
import { EsContestSubmission, IContestStats } from '../../EsContestSubmission/EsContestSubmission';
import { EsTopPerformersCarousel, ITopPerformersCarousel } from '../../EsCarousels/EsTopPerformersCarousel';
import { IAdvancedContest, IContest } from '../../../types/global/helper';
import { Hero } from '../../EsHero/Hero';
import { isEmpty } from '../../../utils/lodash';
import { IContestStatus } from '../../../types/global/generic.types';
import { RedeemableCodeDialogue } from './RedeemableCodeDialogue';
import { StyleSheet } from 'react-native';
import { ColorScheme } from '../../../constants/theme';
import { JuryDutyDialogue } from './JuryDutyDialogue';
import { ContestAwards } from '../../ContestDetails/ContestAwards';
import { HostEntryDialogue } from './HostEntryDialogue';

export interface IHeroMapping extends ITopPerformersCarousel, Omit<IContestStats, 'submissionStat'> {
    contest: IAdvancedContest;
    onToggleStatus?: (status: IContestStatus) => void;
}

const EsContestEngageContainer: React.FC<IHeroMapping> = props => {
    // Set to false to see where no videos have been submitted to the contest
    const { contest, onToggleStatus, onClose } = props;
    const [open, setOpen] = useState(false);
    const [openJuryDutyModal, setOpenJuryDutyModal] = useState(false);
    const [openHostEntryModal, setOpenHostEntryModal] = useState(false);

    return (
        <ThemeContext.Consumer>
            {({ themeStyle }) => {
                const styles = dynamicStyles(themeStyle);
                return (
                    <Container
                        disableGutters
                        style={styles.container}
                        sx={{
                            width: '100%',
                            marginTop: { xs: '16px', sm: '40px' },
                            marginLeft: { xs: '0px', sm: '65px' },
                            paddingLeft: { xs: '16px', sm: '40px' },
                            paddingRight: { xs: '16px', sm: '40px' },
                            overflowX: 'hidden',
                        }}
                        maxWidth={false}>
                        <Container
                            disableGutters
                            maxWidth={false}
                            sx={{
                                zIndex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: '50px',
                                height: '100%',
                                width: '100%',
                                overflowX: 'hidden',
                            }}>
                            <RedeemableCodeDialogue
                                open={open}
                                onClose={() => setOpen(!open)}
                                placeholder="Search by username..."
                                contestId={contest._id}
                            />
                            <JuryDutyDialogue
                                open={openJuryDutyModal}
                                onClose={() => setOpenJuryDutyModal(!openJuryDutyModal)}
                                contestId={contest._id}
                                contest={contest}
                            />
                            <Hero
                                campaign={contest as unknown as IContest}
                                disableJoinContest
                                isDetails
                                onBackButtonPressed={onClose}
                                isAdminMode
                                status={contest.status}
                                onToggleStatus={onToggleStatus}
                            />

                            {!!contest.awards && !isEmpty(contest.awards) && (
                                <ContestAwards contestAwards={contest.awards} />
                            )}

                            <Container
                                disableGutters
                                maxWidth={false}
                                style={styles.container}
                                sx={{
                                    zIndex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    gap: '50px',
                                    height: 'auto',
                                    width: '100%',
                                    overflowX: 'hidden',
                                }}>
                                <EsContestSubmission
                                    {...props}
                                    onClickSendCode={() => setOpen(!open)}
                                    onClickAssignJuryDuty={() => setOpenJuryDutyModal(!openJuryDutyModal)}
                                    onHostEntryClicked={() => setOpenHostEntryModal(!openHostEntryModal)}
                                />
                                {!!contest && (
                                    <HostEntryDialogue
                                        contestId={contest._id}
                                        open={openHostEntryModal}
                                        contest={contest}
                                        onClose={() => setOpenHostEntryModal(!openHostEntryModal)}
                                    />
                                )}
                                {isEmpty(contest.top_performers) ? (
                                    <Container
                                        disableGutters
                                        maxWidth={false}
                                        sx={{
                                            zIndex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '50px',
                                            height: '40vh',
                                            width: '100%',
                                            overflowX: 'hidden',
                                        }}>
                                        <EsNoSubmissions />
                                    </Container>
                                ) : (
                                    <EsTopPerformersCarousel submissionData={contest.top_performers} {...props} />
                                )}
                            </Container>
                        </Container>
                    </Container>
                );
            }}
        </ThemeContext.Consumer>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        container: {
            height: 'auto',
            minHeight: '100vh',
            justifyContent: 'center',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            overflow: 'hidden',
        },
    });

export { EsContestEngageContainer };
